/**
 * Attribute processor for the Now Assist Skill Plugin
 * Handles processing of input and output attributes for skill definitions
 */

import { TABLE_NAMES } from './defaults'
import { applyDefaults } from './skill-plugin-helpers'
import type {
    Context,
    ObjectShape,
    CallExpressionShape,
    Record as BuildRecord,
    Shape,
} from '@servicenow/sdk-build-core'
import type { NowIdShape } from '@servicenow/sdk-build-plugins'
import { normalizeInputFieldName } from './tool-processor'

// Destructure table names for easier usage
const { SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE } = TABLE_NAMES

/**
 * Validate that names contain only alphanumeric characters and spaces
 * @param str - Name to validate (skill name, attribute name, etc.)
 * @param diagnostics - Diagnostics object for error reporting
 * @param source - Source object for error location
 * @param nameType - Type of name being validated (e.g., 'Skill name', 'Attribute name')
 * @returns The original string (validation error will be reported via diagnostics)
 */
export function validateName(
    str: string,
    diagnostics: Context['diagnostics'],
    source: Shape,
    nameType: string = 'Name'
): string {
    // Match AI Studio's validation: only alphanumeric characters and spaces
    const regex = /^[a-zA-Z0-9 ]+$/
    if (!regex.test(str)) {
        // Find and show specific invalid characters
        const invalidChars = str.match(/[^a-zA-Z0-9 ]/g)
        const invalidCharsStr = invalidChars ? [...new Set(invalidChars)].join(', ') : ''

        diagnostics.error(
            source,
            `${nameType} '${str}' contains invalid character(s): ${invalidCharsStr}. ` +
                `${nameType}s must contain only letters, numbers, and spaces. ` +
                `Please remove special characters, underscores, and punctuation.`
        )
    }
    return str
}

/**
 * Generate internal name from display name
 * Converts display names like 'Travel Destination' to 'travel_destination'
 * Matches AI Studio's convertAttributeLabelToName logic exactly
 * @param displayName - The user-friendly display name
 * @returns Clean internal name suitable for database storage
 */
export function generateInternalName(displayName: string): string {
    // Convert to lowercase and replace one or more spaces with underscores
    return displayName.toLowerCase().trim().replace(/\s+/g, '_')
}

/**
 * Helper function to process input/output attributes
 * @param definitionShape - The definition argument shape containing inputs and outputs
 * @returns Object containing attribute records and input names for tool validation
 */
export async function processAttributes(
    definitionShape: ObjectShape,
    capabilityRecord: BuildRecord,
    capabilityDefinitionRecord: BuildRecord,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics'],
    callExpression: CallExpressionShape
): Promise<{ records: BuildRecord[]; inputNames: Set<string> }> {
    const attributeRecords: BuildRecord[] = []
    // Note: attributeOrder removed - platform handles defaults

    // Collect input names for cross-validation with tools
    const inputNames = new Set<string>()

    // Process inputs
    if (definitionShape.has('inputs')) {
        const inputs = definitionShape.get('inputs')
        const inputElements: Shape[] = inputs.isArray() ? inputs.asArray().getElements() : []
        if (inputElements.length > 0 || inputs.isArray()) {
            // Validate: Only one input attribute can have glide_record data type
            const glideRecordInputs: string[] = []
            for (const inputAttr of inputElements) {
                if (inputAttr.isObject()) {
                    const dataType = inputAttr.has('dataType')
                        ? inputAttr.get('dataType')?.asString().getValue()
                        : 'string'
                    if (dataType === 'glide_record') {
                        // Use the display name to generate internal name
                        const displayName = inputAttr.has('name')
                            ? inputAttr.get('name')?.asString().getValue()
                            : 'unknown'
                        glideRecordInputs.push(displayName)
                    }
                }
            }

            if (glideRecordInputs.length > 1) {
                diagnostics.error(
                    inputs,
                    `Only one input attribute can have data_type 'glide_record'. ` +
                        `Found ${glideRecordInputs.length} glide_record inputs: [${glideRecordInputs.join(', ')}]. ` +
                        `The glide_record type is designed to reference a single record. ` +
                        `If you need multiple records, consider using 'json_array' or 'simple_array' data types instead.`
                )
                return { records: [], inputNames }
            }

            // Validate: Input names must be unique (both exact and normalized/lowercase)
            // ServiceNow normalizes input names to lowercase internally, so 'userQuery' and 'userquery' would collide
            const normalizedInputNames = new Map<string, string>() // normalized -> original
            for (const inputAttr of inputElements) {
                if (inputAttr.isObject()) {
                    const displayName = inputAttr.has('name') ? inputAttr.get('name')?.asString().getValue() : 'unknown'
                    const normalizedName = normalizeInputFieldName(displayName ?? 'unknown')

                    // Check for exact duplicate
                    if (inputNames.has(displayName)) {
                        diagnostics.error(
                            inputAttr,
                            `Duplicate input name '${displayName}'. Each input must have a unique name.`
                        )
                        return { records: [], inputNames }
                    }

                    // Check for normalized duplicate (e.g., 'userQuery' vs 'userquery')
                    const existingName = normalizedInputNames.get(normalizedName)
                    if (existingName && existingName !== displayName) {
                        diagnostics.error(
                            inputAttr,
                            `Input name '${displayName}' conflicts with '${existingName}'. ` +
                                `Both normalize to '${normalizedName}' internally. ` +
                                `Input names must be unique when normalized to lowercase.`
                        )
                        return { records: [], inputNames }
                    }

                    inputNames.add(displayName)
                    normalizedInputNames.set(normalizedName, displayName)
                }
            }

            for (const inputAttr of inputElements) {
                if (inputAttr.isObject()) {
                    // Get the display name (API field called 'name')
                    const displayName = inputAttr.has('name') ? inputAttr.get('name')?.asString().getValue() : ''

                    // $id is mandatory in type definitions - TypeScript enforces this at compile time
                    const inputExplicitId = inputAttr.get('$id') as NowIdShape

                    // Validate display name exists and is not empty
                    if (!displayName || displayName.trim() === '') {
                        diagnostics.error(inputAttr, 'Attribute name is required and cannot be empty')
                        continue
                    }

                    // Validate the display name doesn't contain underscores or special characters
                    const validatedDisplayName = validateName(displayName, diagnostics, inputAttr, 'Attribute name')

                    // Generate internal name from display name: 'Travel Destination' -> 'travel_destination'
                    const generatedInternalName = generateInternalName(validatedDisplayName)

                    // Use display name as label, generated name as internal name
                    const label = displayName
                    const description = inputAttr.has('description')
                        ? inputAttr.get('description')?.asString().getValue()
                        : ''

                    // Handle mandatory field - could be boolean or string
                    let mandatory = false
                    if (inputAttr.has('mandatory')) {
                        const mandatoryShape = inputAttr.get('mandatory')
                        const mandatoryRawValue = mandatoryShape?.getValue()
                        mandatory =
                            mandatoryRawValue === true || mandatoryRawValue === 'true' || mandatoryRawValue === '1'
                    }

                    const dataType = inputAttr.has('dataType')
                        ? inputAttr.get('dataType')?.asString().getValue()
                        : 'string'
                    // testValues field maps to default_value column in the database
                    const testValues = inputAttr.has('testValues')
                        ? inputAttr.get('testValues')?.asString().getValue()
                        : ''
                    // Note: order removed - platform handles defaults

                    // Handle truncate field - could be boolean or string
                    let truncate = false
                    if (inputAttr.has('truncate')) {
                        const truncateShape = inputAttr.get('truncate')
                        const truncateRawValue = truncateShape?.getValue()
                        truncate = truncateRawValue === true || truncateRawValue === 'true' || truncateRawValue === '1'
                    }

                    // Validate truncate is not used with unsupported data types
                    const nonTruncatableDataTypes = ['simple_array', 'json_object', 'json_array']
                    if (truncate && nonTruncatableDataTypes.includes(dataType || '')) {
                        diagnostics.error(
                            inputAttr.has('truncate') ? inputAttr.get('truncate') : inputAttr,
                            `Input attribute '${displayName}': The 'truncate' property is not supported for dataType '${dataType}'. ` +
                                `Only the following data types support truncate: string, numeric, boolean, record, glide_record. ` +
                                `Please remove the 'truncate' property or change the dataType.`
                        )
                        return { records: [], inputNames }
                    }

                    // Handle schema field for glide_record data type
                    let schema = ''
                    if (dataType === 'glide_record') {
                        // Check if user provided tableName and tableSysId separately
                        const tableName = inputAttr.has('tableName')
                            ? inputAttr.get('tableName')?.asString().getValue()
                            : ''

                        // Handle tableSysId which can be either a string or Now.Internal.ExplicitKey object
                        let tableSysId = ''
                        if (inputAttr.has('tableSysId')) {
                            const tableSysIdShape = inputAttr.get('tableSysId')
                            const rawValue = tableSysIdShape?.getValue()

                            // Check if it's an ExplicitKey object (has 'id' property)
                            if (rawValue && typeof rawValue === 'object' && 'id' in rawValue) {
                                tableSysId = typeof rawValue.id === 'string' ? rawValue.id : ''
                            } else if (typeof rawValue === 'string') {
                                tableSysId = rawValue
                            }
                        }

                        // Validate: tableName is required for glide_record, tableSysId is required when testValues is provided
                        if (!tableName || (testValues && !tableSysId)) {
                            diagnostics.error(
                                inputAttr,
                                `dataType 'glide_record' requires 'tableName'${testValues ? " and 'tableSysId' when 'testValues' is provided" : ''}. ` +
                                    `Missing: ${!tableName ? 'tableName' : ''}${!tableName && testValues && !tableSysId ? ' and ' : ''}${testValues && !tableSysId ? 'tableSysId' : ''}.`
                            )
                            continue
                        }

                        if (tableName) {
                            // Construct schema JSON from table_name and table_sys_id (sysId defaults to empty string)
                            schema = JSON.stringify({ tableName, sysId: tableSysId || '' })
                        } else if (inputAttr.has('schema')) {
                            // Fallback to schema field if provided
                            const schemaValue = inputAttr.get('schema')
                            if (schemaValue?.isObject()) {
                                // If schema is an object, stringify it
                                schema = JSON.stringify(schemaValue.getValue())
                            } else if (schemaValue?.isString()) {
                                // If schema is already a string, use it directly
                                schema = schemaValue.asString().getValue()
                            }
                        }
                    }

                    const attrRecord = await factory.createRecord({
                        source: inputAttr,
                        table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
                        explicitId: inputExplicitId,
                        properties: arg.transform(({ $ }) => {
                            const properties = {
                                ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
                                name: $.val(generatedInternalName),
                                label: $.val(label),
                                description: $.val(description),
                                capability: $.val(capabilityRecord.getId()),
                                type: $.val('input'),
                                data_type: $.val(dataType),
                                mandatory: $.val(mandatory),
                                truncate: $.val(truncate),
                                default_value: $.val(testValues), // test_values maps to default_value column
                                // NOTE: 'order' and 'hidden' intentionally omitted - let platform handle defaults
                            }

                            // Add schema field if data type is glide_record
                            if (dataType === 'glide_record' && schema) {
                                return { ...properties, schema: $.val(schema) } as typeof properties & {
                                    schema: unknown
                                }
                            }

                            return properties
                        }),
                    })
                    attributeRecords.push(attrRecord)
                }
            }
        }
    }

    // Process outputs (add standard outputs + custom ones)
    const standardOutputs = ['response', 'provider', 'errorcode', 'status', 'error'] as const
    const outputDescriptions = {
        response: 'Response from the skill execution',
        provider: 'Provider information for the skill',
        errorcode: 'Error code if skill execution fails',
        status: 'Status of the skill execution',
        error: 'Error message if skill execution fails',
    }
    const standardOutputLabels = {
        response: 'response',
        provider: 'provider',
        errorcode: 'errorCode',
        status: 'status',
        error: 'error',
    }

    // Add standard outputs first
    for (const outputName of standardOutputs) {
        const attrRecord = await factory.createRecord({
            source: {
                path: callExpression.getOriginalFilePath(),
                content: '',
            },
            table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
            properties: arg.transform(({ $ }) => ({
                ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
                name: $.val(outputName),
                label: $.val(standardOutputLabels[outputName as keyof typeof standardOutputLabels]),
                description: $.val(outputDescriptions[outputName as keyof typeof outputDescriptions] || ''),
                capability: $.val(capabilityRecord.getId()),
                type: $.val('output'),
                // NOTE: 'order', 'hidden', 'contains_large_input' intentionally omitted - let platform handle defaults
            })),
        })
        attributeRecords.push(attrRecord)
    }

    // Process custom outputs if provided
    if (definitionShape.has('outputs')) {
        const outputs = definitionShape.get('outputs')
        if (outputs.isArray()) {
            const outputElements = outputs.asArray().getElements()

            // Validate: Only one output attribute can have glide_record data type
            const glideRecordOutputs: string[] = []
            for (const outputAttr of outputElements) {
                if (outputAttr.isObject()) {
                    const dataType = outputAttr.has('dataType')
                        ? outputAttr.get('dataType')?.asString().getValue()
                        : 'string'
                    if (dataType === 'glide_record') {
                        const displayName = outputAttr.has('name')
                            ? outputAttr.get('name')?.asString().getValue()
                            : 'unknown'
                        glideRecordOutputs.push(displayName)
                    }
                }
            }

            if (glideRecordOutputs.length > 1) {
                diagnostics.error(
                    outputs,
                    `Only one output attribute can have data_type 'glide_record'. ` +
                        `Found ${glideRecordOutputs.length} glide_record outputs: [${glideRecordOutputs.join(', ')}]. ` +
                        `The glide_record type is designed to reference a single record. ` +
                        `If you need multiple records, consider using 'json_array' or 'simple_array' data types instead.`
                )
                return { records: [], inputNames }
            }

            // Validate: Output names must be unique and not conflict with input names
            const outputNames = new Set<string>()
            for (const outputAttr of outputElements) {
                if (outputAttr.isObject()) {
                    const displayName = outputAttr.has('name')
                        ? outputAttr.get('name')?.asString().getValue()
                        : 'unknown'

                    // Check for duplicate output names
                    if (outputNames.has(displayName)) {
                        diagnostics.error(
                            outputAttr,
                            `Duplicate output name '${displayName}'. Each output must have a unique name.`
                        )
                        return { records: [], inputNames }
                    }

                    // Check for conflict with input names
                    if (inputNames.has(displayName)) {
                        diagnostics.error(
                            outputAttr,
                            `Output name '${displayName}' conflicts with an input name. Input and output names must be unique across both.`
                        )
                        return { records: [], inputNames }
                    }

                    outputNames.add(displayName)
                }
            }

            for (const outputAttr of outputElements) {
                if (outputAttr.isObject()) {
                    // Get the display name (API field called 'name')
                    const displayName = outputAttr.has('name') ? outputAttr.get('name')?.asString().getValue() : ''

                    // $id is mandatory in type definitions - TypeScript enforces this at compile time
                    const outputExplicitId = outputAttr.get('$id') as NowIdShape

                    // Validate display name exists and is not empty
                    if (!displayName || displayName.trim() === '') {
                        diagnostics.error(outputAttr, 'Attribute name is required and cannot be empty')
                        continue
                    }

                    // Validate the display name doesn't contain underscores or special characters
                    const validatedDisplayName = validateName(displayName, diagnostics, outputAttr, 'Attribute name')

                    // Generate internal name from display name: 'Travel Response' -> 'travel_response'
                    const generatedInternalName = generateInternalName(validatedDisplayName)

                    // Skip standard outputs
                    if (standardOutputs.includes(generatedInternalName as (typeof standardOutputs)[number])) {
                        continue
                    }

                    const label = displayName
                    const description = outputAttr.has('description')
                        ? outputAttr.get('description')?.asString().getValue()
                        : ''
                    const dataType = outputAttr.has('dataType')
                        ? outputAttr.get('dataType')?.asString().getValue()
                        : 'string'
                    // Note: order removed - platform handles defaults

                    // Handle schema field for glide_record data type
                    let schema = ''
                    if (dataType === 'glide_record') {
                        // Check if user provided tableName and tableSysId separately
                        const tableName = outputAttr.has('tableName')
                            ? outputAttr.get('tableName')?.asString().getValue()
                            : ''

                        // Validate: tableName is required for glide_record outputs
                        if (!tableName) {
                            diagnostics.error(outputAttr, `dataType 'glide_record' requires 'tableName'.`)
                            continue
                        }

                        // Construct schema JSON with tableName and empty sysId
                        schema = JSON.stringify({ tableName, sysId: '' })
                    }

                    const attrRecord = await factory.createRecord({
                        source: outputAttr,
                        table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
                        explicitId: outputExplicitId,
                        properties: arg.transform(({ $ }) => {
                            const properties = {
                                ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
                                name: $.val(generatedInternalName),
                                label: $.val(label),
                                description: $.val(description),
                                capability: $.val(capabilityRecord.getId()),
                                type: $.val('output'),
                                data_type: $.val(dataType),
                                // NOTE: 'order', 'hidden' intentionally omitted - let platform handle defaults
                            }

                            // Add schema field if data type is glide_record
                            if (dataType === 'glide_record' && schema) {
                                return { ...properties, schema: $.val(schema) } as typeof properties & {
                                    schema: unknown
                                }
                            }

                            return properties
                        }),
                    })
                    attributeRecords.push(attrRecord)
                }
            }
        }
    }

    return { records: attributeRecords, inputNames }
}
